'use client';

import { FIELDS } from '@caverne/shared';

interface ScreenSizePickerProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Visuel comparatif des tailles (ramette A4 comme repère). Passé par l'appelant :
   * le design system ignore le `basePath` de l'application.
   */
  imageSrc: string;
}

/**
 * Choix de la taille d'écran par un visuel plutôt que par une diagonale en pouces —
 * personne ne connaît la diagonale de son écran, tout le monde reconnaît une ramette A4.
 */
export function ScreenSizePicker({ value, onChange, imageSrc }: ScreenSizePickerProps) {
  const options = FIELDS.taille.sizeOptions ?? [];

  return (
    <fieldset>
      <legend className="text-secondary mb-2 text-sm font-medium">{FIELDS.taille.label}</legend>

      {/* eslint-disable-next-line @next/next/no-img-element -- composant hors Next, pas d'accès à next/image. */}
      <img
        src={imageSrc}
        alt="Comparatif des tailles d'écran L, M et S, avec une ramette de papier A4 comme repère."
        className="border-secondary mb-3 w-full rounded-lg border"
      />

      <div className="grid grid-cols-3 gap-3">
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              aria-pressed={isSelected}
              className={[
                'flex flex-col items-center gap-1 rounded-lg border px-3 py-3 transition-colors',
                isSelected
                  ? 'border-brand bg-brand-primary ring-1 ring-brand'
                  : 'border-secondary bg-primary hover:bg-secondary',
              ].join(' ')}
            >
              <span className="text-primary text-lg font-semibold">{option.value}</span>
              <span className="text-tertiary text-xs">{option.hint}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
